import { Button } from '@ooh/ui/components/button';
import {
  CHIP_CLASS,
  statusForPeriod,
  type ClientOfferReviewDecision,
  type ClientOfferReviewHalfStatus,
  type ClientOfferReviewPeriod,
  type ClientOfferReviewPeriodChipLabels,
  type ClientOfferReviewUnit,
} from './client-offer-review-types';

function chipLabel(
  status: ClientOfferReviewHalfStatus,
  selected: boolean,
  labels: ClientOfferReviewPeriodChipLabels,
): string {
  if (status === 'sold') return labels.sold;
  if (status === 'reserved') return labels.reserved;
  return selected ? labels.selected : labels.free;
}

/** Shared tooltip/accessible-name text for both the non-pickable `<div>` and pickable
 * `<Button>` chip branches. Appends `willAcceptHint` when clicking this chip would also
 * accept the unit (client-offer-interactive-view / "A chip that would accept its unit
 * says so before the click"), so the consequence is conveyed by pointer (title) and by
 * assistive technology (aria-label) alike. */
function chipTitle(
  period: ClientOfferReviewPeriod,
  status: ClientOfferReviewHalfStatus,
  selected: boolean,
  labels: ClientOfferReviewPeriodChipLabels,
  willAccept: boolean,
): string {
  const base = `${period.title ?? period.label} — ${chipLabel(status, selected, labels)}`;
  return willAccept ? `${base}. ${labels.willAcceptHint}` : base;
}

/**
 * The unit card's period ("klocki") chip strip — the only period picker
 * after the retired unit detail modal (client-offer-interactive-view /
 * Per-Unit Availability Grid with Granularity Toolbar, and the REMOVED "Unit
 * Detail Modal and Photo Lightbox" requirement). A chip is a real
 * `role="switch"` button when the unit is not `locked` (carried forward,
 * binding), the view is not `readOnly`, and the period's own status is
 * `free` — the unit's own decision is NOT a precondition
 * (client-offer-decision-flow: selecting a free chip on an undecided or
 * rejected unit accepts that unit with only the clicked period selected;
 * see `handlePeriodSelect` in use-client-offer-selections.ts for the write).
 * In every other state the strip stays presentational — a plain `<div>`,
 * no click handler — but is still perceivable to assistive technology
 * (status + reason via `aria-label`), not `aria-hidden`. `hint` carries the
 * selected-range summary as plain text for assistive technology.
 */
export function ClientOfferReviewPeriodChips({
  unit,
  periods,
  selectedIds,
  hint,
  labels,
  decision = null,
  locked = false,
  lockedHint,
  readOnly = false,
  roHint,
  onTogglePeriod,
}: {
  unit: ClientOfferReviewUnit;
  periods: ClientOfferReviewPeriod[];
  selectedIds: Set<string>;
  hint: string;
  labels: ClientOfferReviewPeriodChipLabels;
  /** Only an already-accepted unit's chips can ever become selectable. */
  decision?: ClientOfferReviewDecision;
  /** Carried-forward, binding unit — offer-decision-carry-forward. Forces every chip non-pickable. */
  locked?: boolean;
  /** Explanatory tooltip shown on every chip when `locked` is true. */
  lockedHint?: string;
  /** Read-only (preview) view — forces every chip non-pickable. */
  readOnly?: boolean;
  /** Explanatory tooltip shown on every chip when `readOnly` is true. */
  roHint?: string;
  /** Called with the period id and its new selected state when a pickable chip is clicked. */
  onTogglePeriod?: (periodId: string, nowSelected: boolean) => void;
}) {
  const interactive = !locked && !readOnly;
  const disabledHint = locked ? lockedHint : readOnly ? roHint : undefined;

  return (
    <div>
      <div className="flex gap-1 overflow-x-auto pb-0.5">
        {periods.map((p) => {
          const status = statusForPeriod(unit, p);
          const selected = selectedIds.has(p.id);
          const pickable = interactive && status === 'free';
          const willAccept = pickable && decision !== 'accepted';
          const chipClass = selected ? CHIP_CLASS.selected : CHIP_CLASS[status];
          const sharedClassName = `shrink-0 whitespace-nowrap rounded-md border px-1 py-1 text-center text-2xs font-medium ${chipClass}`;

          if (!pickable) {
            const nonPickableName = disabledHint ?? chipTitle(p, status, selected, labels, false);
            return (
              <div
                key={p.id}
                title={nonPickableName}
                aria-label={nonPickableName}
                data-testid={`period-chip-${unit.id}-${p.id}`}
                className={`cursor-default ${sharedClassName}`}
                style={{ minWidth: p.minWidth }}
              >
                {p.label}
              </div>
            );
          }

          const toggleAriaLabel = willAccept
            ? `${labels.toggleAriaLabel(p.label)}. ${labels.willAcceptHint}`
            : labels.toggleAriaLabel(p.label);

          return (
            <Button
              key={p.id}
              type="button"
              variant="outline"
              role="switch"
              aria-checked={selected}
              aria-label={toggleAriaLabel}
              onClick={(e) => {
                e.stopPropagation();
                onTogglePeriod?.(p.id, !selected);
              }}
              title={chipTitle(p, status, selected, labels, willAccept)}
              data-testid={`period-chip-${unit.id}-${p.id}`}
              className={`h-auto min-w-0 cursor-pointer ${sharedClassName}`}
              style={{ minWidth: p.minWidth }}
            >
              {p.label}
            </Button>
          );
        })}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}
